import { Fragment, type ReactNode } from 'react';
import { chakra, HStack, type StackProps } from '@chakra-ui/react';

/**
 * One key, drawn as the key it names.
 *
 * Everything about how it looks is the `keyCap` layer style; what is left here is the box it needs
 * to be — square-ish, centred, and gone where there is no keyboard.
 */
export function KeyCap(props: { children: ReactNode; decorative?: boolean }) {
	return (
		<chakra.kbd
			// Decorative where the control already declares the key through `aria-keyshortcuts`: without
			// this the cap joins the accessible name and a radio announces itself as "1 Incorrect".
			aria-hidden={props.decorative ? 'true' : undefined}
			// Gone below `md`, along with everything else keyboard-shaped.
			display={{ base: 'none', md: 'inline-flex' }}
			layerStyle="keyCap"
			alignItems="center"
			justifyContent="center"
			minW={4}
			h={4}
			px={1}
			// `kbd` defaults to monospace, which at this size reads as a code sample rather than a key.
			fontFamily="inherit"
			fontSize="2xs"
			fontWeight="semibold"
			lineHeight="1"
		>
			{props.children}
		</chakra.kbd>
	);
}

export interface Shortcut {
	/** The key or chord. Absent where the entry is a message rather than a shortcut. */
	key?: string;
	label: string;
}

interface ShortcutHintProps extends Omit<StackProps, 'children'> {
	shortcuts: readonly Shortcut[];
	/** The accessible name. Two hints can share a surface, and a test has to tell them apart. */
	label: string;
}

/**
 * The keys a surface answers to, and what each one does.
 *
 * A flex row rather than a line of text, because a cap is 16px tall in a 12px line and no vertical
 * alignment reads right there — `middle` puts it against the baseline plus half an x-height, which
 * is not where the word beside it sits. Laid out as boxes, cap and word centre on each other and
 * the spacing is a `gap` rather than whatever a space glyph happens to measure.
 *
 * The pairing is what the two gaps carry: a key sits close to the word it belongs to, and the
 * groups sit further apart with a separator between, so "D confirm" reads as one thing.
 *
 * Hidden below `md`, where there is no keyboard to press any of it.
 */
export function ShortcutHint(props: ShortcutHintProps) {
	const { shortcuts, label, ...stackProps } = props;

	return (
		<HStack
			display={{ base: 'none', md: 'flex' }}
			gap={2}
			textStyle="micro"
			color="fg.muted"
			role="note"
			aria-label={label}
			{...stackProps}
		>
			{shortcuts.map((shortcut, index) => (
				// Keyed on both halves: a label alone collides the day two keys share one.
				<Fragment key={`${shortcut.key ?? ''}-${shortcut.label}`}>
					{index > 0 ? (
						<chakra.span color="fg.subtle" aria-hidden="true">
							·
						</chakra.span>
					) : null}
					<HStack gap={1}>
						{shortcut.key ? <KeyCap>{shortcut.key}</KeyCap> : null}
						<chakra.span>{shortcut.label}</chakra.span>
					</HStack>
				</Fragment>
			))}
		</HStack>
	);
}

export default ShortcutHint;
